import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import first from 'lodash/first'
import last from 'lodash/last'
import orderBy from 'lodash/orderBy'
import { pipeWith } from 'pipe-ts'

import { isSameSet, parseDate, shortISODate } from '../__utils'

export function mergeOverlappingBlocks<T extends { homeId: string }>(
  _blocks: T[] = [],
  {
    getStartDate,
    getEndDate,
  }: { getStartDate: (block: T) => string; getEndDate: (block: T) => string },
) {
  return pipeWith(
    _blocks,

    function interpolatePeriods(blocks) {
      return blocks.map(block => {
        const start = parseDate(getStartDate(block))
        const end = parseDate(getEndDate(block))

        if (!start || !end || +start > +end) {
          return {
            block,
            periodCells: [],
          }
        }

        return {
          block,
          periodCells: eachDayOfInterval({
            start,
            end,
          }),
        }
      })
    },

    function getBlocksByDate(blocksWithPeriods) {
      return blocksWithPeriods.reduce(
        (blocksByDate, { periodCells, block }) => {
          periodCells.forEach((date, index, arr) => {
            const $date = shortISODate(date)

            if (index > 0) {
              const am = `${$date}_am`
              blocksByDate[am] = blocksByDate[am] || new Set()
              blocksByDate[am].add(block)
            }

            if (index < arr.length - 1) {
              const pm = `${$date}_pm`
              blocksByDate[pm] = blocksByDate[pm] || new Set()
              blocksByDate[pm].add(block)
            }
          })
          return blocksByDate
        },
        {} as { [k: string]: Set<T> },
      )
    },

    function getOrderedEntries(blocksByDate) {
      return orderBy(Object.entries(blocksByDate), ([k]) => k)
    },

    function groupBlocksByPeriod(blocksByDateEntries) {
      return blocksByDateEntries.reduce(
        (blockGroups, [periodUnit, blocks]) => {
          const currentGroupBlocks = last(blockGroups)?.blocks

          if (isSameSet(currentGroupBlocks, blocks)) {
            last(blockGroups)?.periodCells.push(periodUnit)
          } else {
            blockGroups.push({
              blocks,
              periodCells: [periodUnit],
            })
          }

          return blockGroups
        },
        [] as { blocks: Set<T>; periodCells: string[] }[],
      )
    },

    function summarizePeriods(blockGroupsByPeriod) {
      return blockGroupsByPeriod.map(({ blocks: groupBlocks, periodCells }) => {
        const sortedPeriodCells = periodCells.sort()

        return {
          blocks: groupBlocks,
          period: {
            start: first(sortedPeriodCells)?.split('_')[0],
            end: last(sortedPeriodCells)?.split('_')[0],
          },
        }
      })
    },
  )
}
